import type {
  GlassSegment,
  ProductInstance,
  SaunaDoor,
  Vec2,
  WallSide,
} from '../shared/types';
import { wallThicknessOf } from './dent';
import {
  innerWallDoorSpans,
  innerWallSpanBand,
  isPolyRoom,
  outerDoorSpans,
  polyGlassPolys,
  polyInnerWallGlassPoly,
  polyOpeningBand,
} from './polyRoom';
import { roomDoors } from './door';

export const GLASS_THICKNESS = 10; // 10 mm glass

/** Glass pane draw style, shared by the plan view and the section view so a pane
 *  reads identically in both (same colour, same line thickness). */
export const GLASS_FILL = 'rgba(74, 163, 255, 0.6)';
export const GLASS_STROKE = '#4aa3ff';
export const GLASS_STROKE_WIDTH = 1.5;
/** A pane's own colour, when it has one, is drawn at the same 0.6 alpha as the
 *  default blue — glass reads as glass whatever tint it is given. */
export const GLASS_FILL_ALPHA = 0.6;

/** `#rrggbb` (or `#rgb`) as `rgba(r, g, b, a)`; anything else is passed through. */
export function hexToRgba(hex: string, a: number): string {
  const h = hex.trim();
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(h);
  if (!m) return h;
  const s = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
  const n = parseInt(s, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

/** Fill / stroke of a pane: its own colour when set, else the standard blue. */
export function glassFill(seg: Pick<GlassSegment, 'color'>): string {
  return seg.color ? hexToRgba(seg.color, GLASS_FILL_ALPHA) : GLASS_FILL;
}
export function glassStroke(seg: Pick<GlassSegment, 'color'>): string {
  return seg.color || GLASS_STROKE;
}

interface WallPick {
  wall: WallSide;
  pos: number;
  /** Distance (mm) from the point to the picked wall SEGMENT (clamped along its
   *  span) — lets a caller choose the nearest wall across several rooms. */
  dist: number;
}

/** Full span of a wall, corner to corner — glass may run all the way out. */
function wallSpan(inst: ProductInstance, wall: WallSide): [number, number] {
  const hw = inst.width / 2;
  const hd = inst.depth / 2;
  if (wall === 'front' || wall === 'back') return [-hw, hw];
  return [-hd, hd];
}

/** Whether any glass on `wall` covers position `pos` along that wall. */
function wallGlassReaches(inst: ProductInstance, wall: WallSide, pos: number): boolean {
  return (inst.glass || []).some(
    (g) => g.wall === wall && Math.min(g.start, g.end) <= pos + 1 && Math.max(g.start, g.end) >= pos - 1
  );
}

/** Find the nearest wall of an instance to a local point, within `threshold` mm. */
export function wallHit(inst: ProductInstance, p: Vec2, threshold: number): WallPick | null {
  const hw = inst.width / 2;
  const hd = inst.depth / 2;
  const candidates: Array<{ wall: WallSide; dist: number; pos: number; along: number; lo: number; hi: number }> = [
    { wall: 'front', dist: Math.abs(p.y + hd), pos: p.x, along: p.x, lo: -hw, hi: hw },
    { wall: 'back', dist: Math.abs(p.y - hd), pos: p.x, along: p.x, lo: -hw, hi: hw },
    { wall: 'left', dist: Math.abs(p.x + hw), pos: p.y, along: p.y, lo: -hd, hi: hd },
    { wall: 'right', dist: Math.abs(p.x - hw), pos: p.y, along: p.y, lo: -hd, hi: hd },
  ];
  let best: WallPick | null = null;
  let bestDist = threshold;
  for (const c of candidates) {
    if (c.dist > bestDist) continue;
    if (c.along < c.lo - threshold || c.along > c.hi + threshold) continue;
    const [lo, hi] = wallSpan(inst, c.wall);
    bestDist = c.dist;
    // True distance to the wall segment: the perpendicular distance, plus how far
    // the point overshoots the wall's span. So a click beyond a wall's end reports
    // a larger distance than one squarely on another room's wall — the caller can
    // then pick the room the glass is really on.
    const overrun = c.along - Math.max(c.lo, Math.min(c.hi, c.along));
    best = { wall: c.wall, pos: Math.max(lo, Math.min(hi, c.pos)), dist: Math.hypot(c.dist, overrun) };
  }
  return best;
}

/** Clamp a position to a wall's interior span. */
export function clampToWall(inst: ProductInstance, wall: WallSide, pos: number): number {
  const [lo, hi] = wallSpan(inst, wall);
  return Math.max(lo, Math.min(hi, pos));
}

/**
 * The local-frame outline of a glass segment's 10 mm-thick pane. Glass runs at
 * the middle of the wall thickness and may reach the footprint corners. Where a
 * front/back pane meets a left/right pane in a corner, BOTH panes are mitred on
 * the 45° diagonal so each ends exactly where the other begins — they touch with
 * no overlap and no gap, like a framed-glass corner.
 */
export function glassCornersLocal(inst: ProductInstance, seg: GlassSegment): Vec2[] {
  const hw = inst.width / 2;
  const hd = inst.depth / 2;
  const t = GLASS_THICKNESS / 2;
  const mid = wallThicknessOf(inst) / 2; // glass sits at the middle of the wall thickness
  const a = Math.min(seg.start, seg.end);
  const b = Math.max(seg.start, seg.end);

  if (seg.wall === 'front' || seg.wall === 'back') {
    const c = seg.wall === 'front' ? -hd + mid : hd - mid;
    const outer = seg.wall === 'front' ? c - t : c + t; // edge toward the wall
    const inner = seg.wall === 'front' ? c + t : c - t; // edge toward the room
    const corner = seg.wall === 'front' ? -hd : hd; // y of the wall corners
    const xL = -hw + mid; // left pane centre line
    const xR = hw - mid; // right pane centre line
    const miterL = a <= -hw + 1 && wallGlassReaches(inst, 'left', corner);
    const miterR = b >= hw - 1 && wallGlassReaches(inst, 'right', corner);
    // Outer/inner X at each end: a mitred end is wider on the outer edge.
    const outerL = miterL ? xL - t : a;
    const innerL = miterL ? xL + t : a;
    const outerR = miterR ? xR + t : b;
    const innerR = miterR ? xR - t : b;
    return [
      { x: outerL, y: outer },
      { x: outerR, y: outer },
      { x: innerR, y: inner },
      { x: innerL, y: inner },
    ];
  }

  const c = seg.wall === 'left' ? -hw + mid : hw - mid;
  const outer = seg.wall === 'left' ? c - t : c + t;
  const inner = seg.wall === 'left' ? c + t : c - t;
  const cornerX = seg.wall === 'left' ? -hw : hw;
  const yF = -hd + mid; // front pane centre line
  const yB = hd - mid; // back pane centre line
  const miterF = a <= -hd + 1 && wallGlassReaches(inst, 'front', cornerX);
  const miterB = b >= hd - 1 && wallGlassReaches(inst, 'back', cornerX);
  const outerF = miterF ? yF - t : a;
  const innerF = miterF ? yF + t : a;
  const outerB = miterB ? yB + t : b;
  const innerB = miterB ? yB - t : b;
  return [
    { x: outer, y: outerF },
    { x: outer, y: outerB },
    { x: inner, y: innerB },
    { x: inner, y: innerF },
  ];
}

/** An axis-aligned rectangle from (x0,y0) to (x1,y1). */
function band(x0: number, y0: number, x1: number, y1: number): Vec2[] {
  return [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }];
}

/**
 * The glass pane(s) for a segment, as one or more local-frame polygons — the
 * single rectangle from `glassCornersLocal` for a rectangular room, or the poly
 * pane for a polygonal room.
 */
export function glassPolysLocal(inst: ProductInstance, seg: GlassSegment): Vec2[][] {
  // The ONE dispatch every consumer goes through — plan, hit-test, area, section,
  // 3D — so a pane glazed into an interior partition works everywhere the moment
  // it is understood here.
  if (seg.innerWallId != null) {
    const poly = polyInnerWallGlassPoly(inst, seg);
    return poly.length >= 3 ? [poly] : [];
  }
  if (seg.edge != null) return polyGlassPolys(inst, seg);
  return [glassCornersLocal(inst, seg)];
}

/**
 * Trim wall glass so it never overlaps a door opening (the door is drawn as glass
 * already). A pane crossing a door is split into the parts on either side; a pane
 * fully inside the opening is removed. Runs for every outer-wall door.
 */
export function clampGlassToDoor(inst: ProductInstance): void {
  if (!inst.glass || !inst.glass.length) return;
  const doors = doorGlassSegments(inst);
  const poly = isPolyRoom(inst);
  let segs = inst.glass;
  for (const d of doors) {
    const ds = Math.min(d.start, d.end), de = Math.max(d.start, d.end);
    const out: GlassSegment[] = [];
    for (const g of segs) {
      // Only glass on the same run as the door can overlap it: the door's edge for
      // a polygon room, else the front wall for a rectangular room.
      const sameRun = poly ? g.edge === d.edge : g.wall === 'front';
      if (!sameRun) { out.push(g); continue; }
      const gs = Math.min(g.start, g.end), ge = Math.max(g.start, g.end);
      if (ge <= ds + 1e-6 || gs >= de - 1e-6) { out.push(g); continue; } // clear of the door
      // A door splits the pane in two. The near part keeps the pane's identity; the
      // far part is a NEW pane, so it drops the id (re-minted on demand) — otherwise
      // both halves share one id and the selection halo, Parts row and Delete all
      // act on both at once.
      const near = gs < ds - 1e-6;
      if (near) out.push({ ...g, start: gs, end: ds }); // part up to the near door jamb
      if (ge > de + 1e-6) out.push({ ...g, start: de, end: ge, id: near ? undefined : g.id }); // part from the far door jamb on
    }
    segs = out;
  }
  // A pane and a door in the SAME partition would both open the fill, and the pane
  // would be drawn across the doorway. Partition doors are not glass segments (they
  // cut the wall directly), so they are trimmed against here rather than above.
  for (const iw of inst.innerWalls || []) {
    for (const d of innerWallDoorSpans(inst, iw.id)) {
      const out: GlassSegment[] = [];
      for (const g of segs) {
        if (g.innerWallId !== iw.id) { out.push(g); continue; }
        const gs = Math.min(g.start, g.end), ge = Math.max(g.start, g.end);
        if (ge <= d.start + 1e-6 || gs >= d.end - 1e-6) { out.push(g); continue; }
        const near = gs < d.start - 1e-6;
        if (near) out.push({ ...g, start: gs, end: d.start });
        if (ge > d.end + 1e-6) out.push({ ...g, start: d.end, end: ge, id: near ? undefined : g.id });
      }
      segs = out;
    }
  }
  inst.glass = segs;
}

/** The doors as glass segments (a door pane is always glass), one per outer-wall
 *  door. Inner-wall doors are not glass panes — they cut the partition directly. */
export function doorGlassSegments(inst: ProductInstance): GlassSegment[] {
  return doorGlassSpans(inst).map((d) => d.seg);
}

/** `doorGlassSegments` with the door each segment is. A segment carries its
 *  door's own height, so `glassPaneTop` of it is the door's top (`doorTop`). */
export function doorGlassSpans(inst: ProductInstance): Array<{ door: SaunaDoor; seg: GlassSegment }> {
  const own = (d: SaunaDoor): Pick<GlassSegment, 'height'> => (d.height != null ? { height: d.height } : {});
  if (isPolyRoom(inst)) {
    return outerDoorSpans(inst).map((d) => ({ door: d.door, seg: { wall: 'front' as const, start: d.start, end: d.end, edge: d.edge, ...own(d.door) } }));
  }
  return roomDoors(inst)
    .filter((d) => d.innerWallId == null)
    .map((d) => ({ door: d, seg: { wall: 'front' as const, start: d.offset - d.width / 2, end: d.offset + d.width / 2, ...own(d) } }));
}

/** Back-compat: the primary door's glass segment, or null. */
export function doorGlassSegment(inst: ProductInstance): GlassSegment | null {
  return doorGlassSegments(inst)[0] ?? null;
}

/**
 * The local-frame rectangle of an OPENING: the full wall-thickness band over the
 * segment (an opening spans the whole wall depth, unlike the thin glass pane).
 * A segment glazed into a partition is the partition's band over it — never the
 * rectangular `wall` band its unused `wall: 'front'` would name.
 */
export function openingBandLocal(inst: ProductInstance, seg: GlassSegment): Vec2[] {
  if (seg.innerWallId != null) {
    const iw = (inst.innerWalls ?? []).find((w) => w.id === seg.innerWallId);
    return (iw && innerWallSpanBand(inst, iw, seg.start, seg.end)) || [];
  }
  if (seg.edge != null) return polyOpeningBand(inst, seg);
  const hw = inst.width / 2, hd = inst.depth / 2, w = wallThicknessOf(inst);
  const a = Math.min(seg.start, seg.end), b = Math.max(seg.start, seg.end);
  if (seg.wall === 'front') return band(a, -hd, b, -hd + w);
  if (seg.wall === 'back') return band(a, hd - w, b, hd);
  if (seg.wall === 'left') return band(-hw, a, -hw + w, b);
  return band(hw - w, a, hw, b); // right
}

/** Index of the opening hit by a local point (with margin), or -1. */
export function openingHitLocal(inst: ProductInstance, p: Vec2, margin: number): number {
  const segs = inst.openings || [];
  for (let i = segs.length - 1; i >= 0; i--) {
    const c = openingBandLocal(inst, segs[i]);
    const minX = Math.min(...c.map((q) => q.x)) - margin;
    const maxX = Math.max(...c.map((q) => q.x)) + margin;
    const minY = Math.min(...c.map((q) => q.y)) - margin;
    const maxY = Math.max(...c.map((q) => q.y)) + margin;
    if (p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY) return i;
  }
  return -1;
}

/** Index of the user glass segment hit by a local point (with margin), or -1. */
export function glassHitLocal(inst: ProductInstance, p: Vec2, margin: number): number {
  const segs = inst.glass || [];
  for (let i = segs.length - 1; i >= 0; i--) {
    const c = glassPolysLocal(inst, segs[i]).flat();
    const minX = Math.min(...c.map((q) => q.x)) - margin;
    const maxX = Math.max(...c.map((q) => q.x)) + margin;
    const minY = Math.min(...c.map((q) => q.y)) - margin;
    const maxY = Math.max(...c.map((q) => q.y)) + margin;
    if (p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY) return i;
  }
  return -1;
}

/**
 * Scale glass segments in place when the sauna footprint changes, so glazing
 * stays proportionate: front/back panes scale with width, left/right with depth.
 */
export function scaleGlass(glass: GlassSegment[], fx: number, fy: number): void {
  for (const g of glass) {
    const f = g.wall === 'left' || g.wall === 'right' ? fy : fx;
    g.start *= f;
    g.end *= f;
  }
}

/**
 * Merge overlapping or touching glass segments on the same wall into single
 * panes, so glazing the same span twice counts only once.
 */
export function mergeGlassSegments(segments: GlassSegment[]): GlassSegment[] {
  // Group by wall — or by polygon edge when the segments are edge-tagged — so a
  // run is only merged with others on the same wall/edge.
  const byWall = new Map<string, GlassSegment[]>();
  for (const s of segments) {
    const a = Math.min(s.start, s.end);
    const b = Math.max(s.start, s.end);
    if (b - a < 1e-6) continue; // ignore zero-length
    // A pane in an interior partition is keyed by that partition, so it merges
    // only with others in the SAME partition and never with a room-wall pane.
    const key = s.innerWallId != null ? `w${s.innerWallId}` : s.edge != null ? `e${s.edge}` : s.wall;
    const arr = byWall.get(key) ?? [];
    // Carry EVERY field (id, colour, height, the wall-dimension pins), not just the
    // geometry: this is called on the whole list each time a pane is added, so
    // rebuilding a bare segment here silently wiped a pane's colour/height and
    // dropped its id (breaking its selection) and its pinned figure.
    arr.push({ ...s, start: a, end: b });
    byWall.set(key, arr);
  }
  const out: GlassSegment[] = [];
  for (const arr of byWall.values()) {
    arr.sort((x, y) => x.start - y.start);
    let cur = { ...arr[0] };
    for (let i = 1; i < arr.length; i++) {
      if (arr[i].start <= cur.end + 1e-6) {
        cur.end = Math.max(cur.end, arr[i].end);
      } else {
        out.push(cur);
        cur = { ...arr[i] };
      }
    }
    out.push(cur);
  }
  return out;
}
